import { useState, useEffect, useCallback, useRef } from 'react';

const DRAFT_PREFIX = 'vetcollab_draft_';

/**
 * Low-level utility methods for localStorage draft persistence.
 */
export const draftStore = {
  saveDraft: (key, data) => {
    try {
      const payload = {
        updatedAt: new Date().toISOString(),
        data,
      };
      localStorage.setItem(`${DRAFT_PREFIX}${key}`, JSON.stringify(payload));
    } catch (e) {
      console.warn('Failed to save draft to localStorage:', e);
    }
  },

  loadDraft: (key) => {
    try {
      const raw = localStorage.getItem(`${DRAFT_PREFIX}${key}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.data ?? null;
    } catch (e) {
      console.warn('Failed to load draft from localStorage:', e);
      return null;
    }
  },

  clearDraft: (key) => {
    try {
      localStorage.removeItem(`${DRAFT_PREFIX}${key}`);
    } catch (e) {
      console.warn('Failed to clear draft from localStorage:', e);
    }
  },

  hasDraft: (key) => {
    try {
      return localStorage.getItem(`${DRAFT_PREFIX}${key}`) !== null;
    } catch (e) {
      return false;
    }
  },
};

/**
 * React hook to auto-save and manage draft state for forms and multi-step wizards.
 *
 * @param {string} key - Unique key for the draft (e.g. 'share_case_user123')
 * @param {Object} initialValue - Initial state if no draft is loaded
 * @param {number} debounceMs - Auto-save debounce delay in ms (default: 500ms)
 */
export function useDraft(key, initialValue, debounceMs = 500) {
  const [data, setData] = useState(initialValue);
  const [hasUnsavedDraft, setHasUnsavedDraft] = useState(false);
  const isFirstRender = useRef(true);

  // Check if a saved draft exists on initial load
  useEffect(() => {
    if (key && draftStore.hasDraft(key)) {
      setHasUnsavedDraft(true);
    }
  }, [key]);

  // Debounced auto-save effect
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!key) return;

    const timer = setTimeout(() => {
      draftStore.saveDraft(key, data);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [key, data, debounceMs]);

  const restoreDraft = useCallback(() => {
    if (!key) return false;
    const saved = draftStore.loadDraft(key);
    if (saved) {
      setData(saved);
      setHasUnsavedDraft(false);
      return true;
    }
    return false;
  }, [key]);

  const discardDraft = useCallback(() => {
    if (!key) return;
    draftStore.clearDraft(key);
    setHasUnsavedDraft(false);
  }, [key]);

  const clearDraft = useCallback(() => {
    if (!key) return;
    draftStore.clearDraft(key);
    setHasUnsavedDraft(false);
  }, [key]);

  return {
    data,
    setData,
    hasUnsavedDraft,
    restoreDraft,
    discardDraft,
    clearDraft,
  };
}

export default useDraft;
